import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import type { TextInput } from "react-native";
import { formatCents, parseCents } from "../../utils/formatters";
import { Input, type InputProps } from "../Input";
const CENTS_FRACTION_DIGITS = 2;
export type CurrencyInputProps = Omit<InputProps,
// Owned by this component; the discriminated trailing-icon union is omitted
// so the prop type collapses to the plain base (otherwise `Omit` distributes
// over the union and TS can't reassemble it for the inner `<Input>`).
"value" | "defaultValue" | "onChangeText" | "onChange" | "prefix" | "inputMode" | "keyboardType" | "rightIcon" | "rightIconRef" | "rightIconLabel" | "onRightIconPress" | "clearable" | "onClear"> & {
  /** Non-negative integer cents (e.g. 123456 for $1,234.56). Controlled —
   *  there is no uncontrolled mode; `undefined` is the empty value. */
  value: number | undefined;
  onValueChange?: (cents: number | undefined) => void;
};

/** Editable display has no symbol — that lives in the field prefix. */
const formatDisplay = (cents: number | undefined): string => formatCents(cents, {
  currencySymbol: ""
});

/**
 * Strip everything except digits and a decimal point — minus signs included,
 * since amounts are non-negative. Keeps only the first decimal group
 * (`1.2.3` → `1.2`) and clamps the fractional part to cents so the shown
 * text and the emitted cents never disagree mid-edit (`1.999` → `1.99`).
 */
const sanitizeInput = (text: string): string => {
  // Collapse runs of dots so `1..2` reads as a single decimal point.
  const digitsAndDots = text.replace(/[^\d.]/g, "").replace(/\.{2,}/g, ".");
  const dotIndex = digitsAndDots.indexOf(".");
  let whole: string;
  let fraction: string;
  if (dotIndex === -1) {
    whole = digitsAndDots;
    fraction = "";
  } else {
    whole = digitsAndDots.slice(0, dotIndex);
    // Only the first decimal group: digits between the first dot and the next
    // dot (later dots + their digits are dropped, not merged), capped to cents.
    const afterFirstDot = digitsAndDots.slice(dotIndex + 1);
    const nextDot = afterFirstDot.indexOf(".");
    const firstGroup = nextDot === -1 ? afterFirstDot : afterFirstDot.slice(0, nextDot);
    fraction = firstGroup.slice(0, CENTS_FRACTION_DIGITS);
  }
  const dot = dotIndex === -1 ? "" : ".";
  return `${whole}${dot}${fraction}`;
};
export const CurrencyInput = forwardRef<TextInput | null, CurrencyInputProps>(function CurrencyInput({
  value,
  onValueChange,
  onFocus,
  onBlur,
  placeholder = "0.00",
  ...inputProps
}, forwardedRef) {
  const [display, setDisplay] = useState(() => formatDisplay(value));
  const isFocusedRef = useRef(false);

  // Controlled: keep the display in sync with the cents source of truth, but
  // never while focused (so typing isn't interrupted). Reformat when the
  // incoming cents differ from what the display currently parses to.
  useEffect(() => {
    if (isFocusedRef.current) {
      return;
    }
    if (parseCents(display) !== value) {
      setDisplay(formatDisplay(value));
    }
  }, [value, display]);
  const handleChangeText = useCallback((text: string) => {
    // Light reformat on change: sanitize but don't re-group, so the cursor
    // doesn't jump while typing. Sanitize clamps the fraction to cents, so
    // the shown text and emitted cents stay consistent.
    const sanitized = sanitizeInput(text);
    setDisplay(sanitized);
    onValueChange?.(parseCents(sanitized));
  }, [onValueChange]);
  const handleFocus = useCallback((event: Parameters<NonNullable<typeof onFocus>>[0]) => {
    isFocusedRef.current = true;
    // Ungroup while editing so the first keystroke doesn't shift the caret.
    setDisplay(sanitizeInput);
    onFocus?.(event);
  }, [onFocus]);
  const handleBlur = useCallback((event: Parameters<NonNullable<typeof onBlur>>[0]) => {
    isFocusedRef.current = false;
    setDisplay(formatDisplay(parseCents(display)));
    onBlur?.(event);
  }, [display, onBlur]);
  return <Input ref={forwardedRef} inputMode="decimal" prefix="$" placeholder={placeholder} value={display} onChangeText={handleChangeText} onFocus={handleFocus} onBlur={handleBlur} {...inputProps} />;
});